import { Building2, Contact, Fuel, IdCard, Store, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { LEASING_ACCESS_PERMISSION } from '&/features/leasing/paths';

/**
 * The "standard" Territory Settings tabs (SWOOP-430): each is gated by a single `:list` permission and carries
 * no extra rule. General, Billing and Leasing are deliberately NOT here — General's access is
 * `territories:edit|manage` (see `canAccessTerritoryGeneral`), Billing is gated on `billing-settings:list`, and
 * Leasing is module-gated — so each is built separately in `territory-detail-page`.
 */
export interface TerritoryTabDescriptor {
  /** The lucide icon component; rendered as `<icon className="h-4 w-4" />`. */
  icon: LucideIcon;
  /** Translation key in the `settings` namespace. */
  labelKey: string;
  /** The permission that both shows the tab and grants access to it. */
  permission: string;
  /** The route path (with the `$territoryId` param). */
  to: string;
}

export const STANDARD_TERRITORY_TABS: readonly TerritoryTabDescriptor[] = [
  {
    icon: Users,
    labelKey: 'territories.settings.tabs.users',
    permission: 'users:list',
    to: '/settings/territories/$territoryId/users',
  },
  {
    icon: Store,
    labelKey: 'territories.settings.tabs.locations',
    permission: 'locations:list',
    to: '/settings/territories/$territoryId/locations',
  },
  {
    icon: Building2,
    labelKey: 'territories.settings.tabs.suppliers',
    permission: 'suppliers:list',
    to: '/settings/territories/$territoryId/suppliers',
  },
  {
    icon: Contact,
    labelKey: 'territories.settings.tabs.customers',
    permission: 'customers:list',
    to: '/settings/territories/$territoryId/customers',
  },
  {
    icon: IdCard,
    labelKey: 'territories.settings.tabs.drivers',
    permission: 'drivers:list',
    to: '/settings/territories/$territoryId/drivers',
  },
  {
    icon: Fuel,
    labelKey: 'territories.settings.tabs.energySourcePrices',
    permission: 'energy-sources-price:list',
    to: '/settings/territories/$territoryId/energy-source-prices',
  },
];

/**
 * Every permission that opens a territory tab OTHER than Billing, EXCEPT General access
 * (`territories:edit|manage`, handled by `canAccessTerritoryGeneral`): the standard tabs' `:list` codes plus
 * `leasing:access`. ONE source — the tabs render from {@link STANDARD_TERRITORY_TABS} and the billing-only
 * check ({@link isBillingOnlyTerritoryViewer}) excludes anyone holding one, so a new tab cannot be added in a
 * single place and silently force a stacked role off it.
 */
export const NON_BILLING_TERRITORY_TAB_PERMISSIONS: readonly string[] = [
  ...STANDARD_TERRITORY_TABS.map((tab) => tab.permission),
  LEASING_ACCESS_PERMISSION,
];
